import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Space, Typography, Switch } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { sendBroadcastMessage, updateNotificationSetting } from '../utils/fetch';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

/**
 * Форма для массовой рассылки сообщений
 * @param {Array} departments - список кабинетов для выбора
 * @param {Array} notifications - все настройки уведомлений (для получения состояния массовой рассылки)
 * @param {Function} onBroadcastToggle - callback для обновления списка уведомлений после переключения
 */
const BroadcastMessageForm = ({ departments = [], notifications = [], onBroadcastToggle }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [broadcastEnabled, setBroadcastEnabled] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);

  // Загрузить состояние массовой рассылки из настроек
  useEffect(() => {
    const broadcastSetting = notifications.find(n => n.type === 'BROADCAST_ENABLED');
    if (broadcastSetting) {
      setBroadcastEnabled(broadcastSetting.isEnabled);
    }
  }, [notifications]);

  // Обработчик переключения массовой рассылки
  const handleToggleBroadcast = async (checked) => {
    setToggleLoading(true);
    try {
      await updateNotificationSetting('BROADCAST_ENABLED', checked);
      setBroadcastEnabled(checked);
      // Обновить родительский компонент
      if (onBroadcastToggle) {
        onBroadcastToggle(checked);
      }
    } catch (error) {
      console.error('Error updating broadcast status:', error);
      // Не меняем состояние при ошибке
    } finally {
      setToggleLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!broadcastEnabled) {
      return;
    }
    
    setLoading(true);
    try {
      await sendBroadcastMessage(values.message, values.recipients);
      form.resetFields();
    } catch (error) {
      console.error('Error sending broadcast:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      style={{
        borderRadius: '8px',
        background: '#fafafa',
      }}
      bodyStyle={{ padding: '24px' }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <div style={{ flex: 1 }}>
              <Title level={4} style={{ margin: 0, marginBottom: '8px' }}>
                Массовая рассылка
              </Title>
              <Text type="secondary">
                Отправьте сообщение всем пользователям или выбранным кабинетам
              </Text>
            </div>
            <div style={{ marginLeft: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text>Включить рассылку:</Text>
              <Switch
                checked={broadcastEnabled}
                onChange={handleToggleBroadcast}
                loading={toggleLoading}
                checkedChildren="Вкл"
                unCheckedChildren="Выкл"
              />
            </div>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            recipients: ['ALL'],
          }}
        >
          {/* Выбор получателей */}
          <Form.Item
            label="Получатели"
            name="recipients"
            rules={[{ required: true, message: 'Выберите получателей' }]}
          >
            <Select
              mode="multiple"
              placeholder="Выберите получателей"
              style={{ width: '100%' }}
              allowClear
              disabled={!broadcastEnabled}
            >
              <Option value="ALL">Все пользователи</Option>
              {departments.map((dept) => (
                <Option key={dept.type} value={dept.type}>
                  {dept.name} ({dept.phoneNumber})
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Текст сообщения */}
          <Form.Item
            label="Сообщение"
            name="message"
            rules={[
              { required: true, message: 'Введите текст сообщения' },
              { min: 10, message: 'Сообщение должно содержать минимум 10 символов' },
            ]}
          >
            <TextArea
              rows={6}
              placeholder="Введите текст сообщения для рассылки..."
              showCount
              maxLength={1000}
              disabled={!broadcastEnabled}
            />
          </Form.Item>

          {/* Кнопка отправки */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={loading}
              size="large"
              block
              disabled={!broadcastEnabled}
            >
              Отправить сообщение
            </Button>
          </Form.Item>
        </Form>
      </Space>
    </Card>
  );
};

export default BroadcastMessageForm;

